import React, { Fragment, useRef } from 'react';
import Link from 'next/link';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import styles from './OfferCardsBlock.styles';
import ctaButtonStyles from '@components/CtaButton/CtaButton.styles';
import Icon from '@components/icons/Icon';
import { CartButton } from '@components/CartButton';
import PromoLabels from '@components/ProductCardNew/PromoLabels/PromoLabels';
import { SectionHeader } from '@components/SectionHeader';
import { FLAGS_TYPES_NAMES } from '@features/categories/const';
import { IProductObject, TProductFlagsKey } from '@features/categories/typescript/index';
import SpacingBlock from '@features/cms/components/collections/SpacingBlock/SpacingBlock';
import theme from '@styles/theme';
import { IParsedImage } from '@components/typescript';
import { splitTextByLineBreaks } from '@utils/splitTextByLineBreaks';
export type LabelType = 'new' | 'promotion' | 'bestseller';
export interface OfferCard {
  id: string;
  title: string;
  subtitle?: string | null;
  text?: string | null;
  href: string;
  image?: string;
  brandLogo?: string | null;
  labelType?: LabelType | null;
  products?: IProductObject[] | null;
}
export type CardVariant = 'default' | 'story';
export interface OfferCardsBlockData {
  title?: string;
  titleLink?: string;
  cards: OfferCard[];
  columns?: 2 | 3 | 4 | 6;
  mobileColumns?: 1 | 2;
  showCardArrows?: boolean;
  cardVariant?: CardVariant;
  backgroundColor?: string;
  marginTop?: number;
  marginBottom?: number;
  background?: string;
  code?: string;
}
export interface OfferCardsBlockProps {
  data: OfferCardsBlockData;
}
const VALID_GRID_COLUMNS = new Set<number>([2, 3, 4, 6]);
const normalizeGridColumns = (value: unknown, fallback: 2 | 3 | 4 | 6): 2 | 3 | 4 | 6 => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : typeof value === 'number' ? value : NaN;
  if (Number.isFinite(n) && VALID_GRID_COLUMNS.has(n)) return n as 2 | 3 | 4 | 6;
  return fallback;
};
const normalizeMobileColumns = (value: unknown, gridColumns: 2 | 3 | 4 | 6): 1 | 2 => {
  if (value === 1 || value === 2) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (n === 1 || n === 2) return n as 1 | 2;
  }
  return gridColumns <= 3 ? 1 : 2;
};
const OfferCardItem = ({
  card,
  columns,
  mobileColumns = 2,
  showArrow = true,
  variant = 'default'
}: {
  card: OfferCard;
  columns?: 2 | 3 | 4 | 6;
  mobileColumns?: 1 | 2;
  showArrow?: boolean;
  variant?: CardVariant;
}) => {
  const cardRef = useRef<HTMLAnchorElement>(null);

  // Map labelType to FLAGS_TYPES_NAMES
  // 'new' -> shows "Nowość" (white label)
  // 'promotion' -> shows "Promocja" (red label)

  const checkFlagActive = (flagName: TProductFlagsKey): boolean => {
    if (!card.labelType) return false;
    if (card.labelType === 'new') return flagName === FLAGS_TYPES_NAMES.novelty;
    if (card.labelType === 'promotion') return flagName === FLAGS_TYPES_NAMES.promotion;
    if (card.labelType === 'bestseller') return flagName === FLAGS_TYPES_NAMES.bestseller;
    return false;
  };
  const isStory = variant === 'story';
  const getImage = () => {
    if (isStory && card?.image) {
      return {
        image: {
          desktop: card?.image || null,
          tablet: card?.image || null,
          mobile: card?.image || null
        }
      } as IParsedImage;
    }
    return null;
  };
  const renderContent = (underline = true) => <>
      {/* Background image */}
      {card.image && <ResponsiveImage className="card-bg-image" image={card.image} alt={card.title} sx={styles.cardImage} />}

      {/* PromoLabels */}
      {card.labelType && <PromoLabels configurable={false} pricePromoPercent="" checkFlagActive={checkFlagActive} isSmall={false} transparent={false} className="promo-label" isBlock={true} />}

      {/* Story variant - cart button on left, title on right */}
      {isStory ? <>
          <div sx={styles.storyContent}>
            <span sx={styles.storyTitle}>{card.title}</span>
            {(card.subtitle || card.text) && <span sx={styles.storySubtitle(underline)}>{card.subtitle || card.text}</span>}
          </div>
        </> : <>
          {/* Content wrapper for hover animation */}
          <div className="card-content-wrapper" sx={styles.cardContentWrapper(columns)}>
            {/* Brand logo */}
            {card.brandLogo && <div sx={styles.brandLogo}>
                <ResponsiveImage image={card.brandLogo} alt="Brand logo" />
              </div>}

            {/* Content */}
            <div sx={styles.cardContent}>
              <span className="card-title" sx={styles.cardTitle(columns)}>
                {splitTextByLineBreaks(card.title).map((line, index) => <Fragment key={index}>
                    {index > 0 ? <br /> : null}
                    {line}
                  </Fragment>)}
              </span>
            </div>
          </div>

          {/* Arrow - outside wrapper, stays in place */}
          {showArrow && <span className="card-arrow" sx={styles.cardArrow(columns)}>
              <Icon icon="ChevronRight" size="1em" />
            </span>}

          {/* Hover button - outside wrapper, positioned absolutely */}
          <span className="card-button" sx={{
        ...ctaButtonStyles.button('outline'),
        ...styles.cardButton(columns)
      }}>
            <span>więcej</span>
          </span>
        </>}
    </>;
  return <div sx={styles.cardWrapper(columns, mobileColumns)}>
      {isStory && card?.image && card.products && card.products.length > 0 && <div sx={styles.storyCartButton}>
          <CartButton label={card.subtitle || 'zobacz produkty'} image={getImage()} products={card?.products || []} />
        </div>}
      {card.href && card.href !== '' ? <Link href={card.href} sx={styles.card(columns)} ref={cardRef}>
          {renderContent()}
        </Link> : <div sx={styles.card(columns)}>{renderContent(false)}</div>}
    </div>;
};
const OfferCardsBlock = ({
  data
}: OfferCardsBlockProps) => {
  const {
    title,
    titleLink,
    cards,
    columns: columnsRaw,
    mobileColumns: mobileColumnsRaw,
    showCardArrows = true,
    cardVariant = 'default',
    backgroundColor,
    marginTop,
    marginBottom,
    background,
    code
  } = data;
  const columns = normalizeGridColumns(columnsRaw, 4);
  const mobileColumns = normalizeMobileColumns(mobileColumnsRaw, columns);
  const spacingBg = background || backgroundColor || theme.rawColors.neutralWhite;
  return <>
      {Number(marginTop) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginTop)),
      code: 'mt'
    }} />}
      <section sx={styles.section(background || backgroundColor || theme.rawColors.neutralWhite)} data-testid={code}>
        <div sx={styles.container}>
          {/* Header */}
          {title && <>
              <div sx={styles.headerWrapper} data-testid="OfferCardsBlock-headerWrapper">
                <SectionHeader title={title} sx={styles.headerTitle} />
                {titleLink && <Link href={titleLink} sx={styles.headerArrow} data-testid="OfferCardsBlock-headerArrow">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>}
              </div>
              <SpacingBlock data={{
            type: 'spacingBlock',
            backgroundColor: background || backgroundColor || theme.rawColors.neutralWhite,
            height: '40',
            code: 'spacing-1'
          }} />
            </>}

          {/* Cards grid */}
          <div sx={styles.grid}>
            {cards.map(card => {
            return <React.Fragment key={card.id}>
                  <OfferCardItem card={card} columns={columns} mobileColumns={mobileColumns} showArrow={showCardArrows} variant={cardVariant} />
                </React.Fragment>;
          })}
          </div>
        </div>
      </section>
      {Number(marginBottom) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginBottom)),
      code: 'mb'
    }} />}
    </>;
};
export default OfferCardsBlock;